import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useStatsigClient } from '@statsig/react-bindings';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ANALYTICS_EVENTS } from '@/shared/analytics/events';
import type {
  V1ListUserNotificationsResponse,
  V1UserNotification,
} from '@/generated/hooks/herald/models';
import {
  getGetUserUnreadNotificationCountForWorkspaceQueryKey,
  getListUserNotificationsQueryKey,
  getStreamUserNotificationsQueryKey,
  listUserNotifications,
  useGetUserUnreadNotificationCountForWorkspace,
  useMarkNotificationsTillNowRead,
} from '@/generated/hooks/herald/v1/user_notifications';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SidebarMenuButton,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from '@kognitos/lattice';

import { queryClient } from '@/providers';
import { getRoutePath } from '@/routes/util';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { captureException } from '@/modules/sentry';
import {
  useStream,
  EStreamProtocol,
  ERetryStrategy,
  type IStreamError,
} from '@/shared/streaming';
import { showToast } from '@/shared/utils/toast-utils';
import SidebarRowContent from '@/layouts/AppSidebar/SidebarRowContent';
import { useNavigate } from 'react-router-dom';
import { NotificationList } from './NotificationList';

/**
 * Shape of a single SSE message from the notification stream.
 */
interface INotificationStreamEvent {
  user_notification?: V1UserNotification;
}

type NotificationTab = 'all' | 'unread';

interface NotificationCenterProps {
  isCollapsed: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}

const PAGE_SIZE = 20;
const NOTIFICATION_STREAM_RECONNECT_MS = 5_000;

export function NotificationCenter({
  isCollapsed,
  onDropdownOpenChange,
}: NotificationCenterProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');
  const { logEvent } = useStatsigClient();
  const { getAccessTokenSilently } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>('unread');
  const [streamedNotifications, setStreamedNotifications] = useState<
    V1UserNotification[]
  >([]);

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const filter = activeTab === 'unread' ? 'is_read=false' : undefined;

  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const invalidateUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetUserUnreadNotificationCountForWorkspaceQueryKey(
        orgId,
        workspaceId,
      ),
    });
  }, [orgId, workspaceId]);

  const handleStreamEvent = useCallback(
    (data: INotificationStreamEvent) => {
      invalidateUnreadCount();

      if (!isOpenRef.current) {
        queryClient.invalidateQueries({
          queryKey: getListUserNotificationsQueryKey(orgId, workspaceId),
        });
        return;
      }

      const notification = data.user_notification;
      if (!notification?.id) return;

      setStreamedNotifications(prev => {
        if (prev.some(n => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });
    },
    [orgId, workspaceId, invalidateUnreadCount],
  );

  const streamUrl =
    orgId && workspaceId
      ? getStreamUserNotificationsQueryKey(orgId, workspaceId)[0]
      : null;

  useStream<INotificationStreamEvent>(
    streamUrl
      ? {
          url: streamUrl,
          protocol: EStreamProtocol.SSE,
          auth: {
            getToken: () => getAccessTokenSilently(),
            e2eTestMode: isE2ETestMode(),
          },
          retry: {
            // Backend disconnects the connection every few seconds and it is expected from the client to retry.
            // This is an always-on notification channel that should keep reconnecting while the app is open.
            maxAttempts: Infinity,
            strategy: ERetryStrategy.EXPONENTIAL,
            baseDelayMs: NOTIFICATION_STREAM_RECONNECT_MS,
            maxDelayMs: 60_000,
          },
          observability: {
            module: 'notifications',
            operation: 'stream_user_notifications',
          },
          onMessage: handleStreamEvent,
          // Explicit onError alongside observability to ensure Sentry capture
          onError: (streamError: IStreamError) => {
            captureException(streamError.error, {
              tags: {
                module: 'notifications',
                action: 'stream_user_notifications',
              },
              extra: {
                errorType: streamError.type,
                reason: streamError.reason,
                httpStatus: streamError.httpStatus,
              },
            });
          },
        }
      : null,
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<
    AxiosResponse<V1ListUserNotificationsResponse>,
    Error,
    InfiniteData<
      AxiosResponse<V1ListUserNotificationsResponse>,
      string | undefined
    >,
    ReturnType<typeof getListUserNotificationsQueryKey>,
    string | undefined
  >({
    queryKey: getListUserNotificationsQueryKey(orgId, workspaceId, {
      filter,
      page_size: PAGE_SIZE,
    }),
    queryFn: ({ pageParam, signal }) =>
      listUserNotifications(
        orgId,
        workspaceId,
        { filter, page_size: PAGE_SIZE, page_token: pageParam },
        { signal },
      ),
    enabled: isOpen && !!orgId && !!workspaceId,
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.data.next_page_token || undefined,
  });

  // Separate query for unread count (always active when org is set)
  const { data: unreadCountData } =
    useGetUserUnreadNotificationCountForWorkspace(orgId, workspaceId);

  const notifications: V1UserNotification[] = useMemo(() => {
    const queryNotifications =
      data?.pages.flatMap(page => page.data.user_notifications ?? []) ?? [];

    if (streamedNotifications.length === 0) return queryNotifications;

    // Prepend streamed notifications, deduplicating against query data
    const queryIds = new Set(queryNotifications.map(n => n.id));
    const newFromStream = streamedNotifications.filter(
      n => !queryIds.has(n.id),
    );
    return [...newFromStream, ...queryNotifications];
  }, [data, streamedNotifications]);

  const unreadCount = unreadCountData?.data?.unread_count ?? 0;
  const hasUnread = unreadCount > 0;

  const handleTabChange = (value: string) => {
    logEvent(ANALYTICS_EVENTS.NOTIFICATION_TAB_SWITCHED, '', {
      tab: value,
    });
    setActiveTab(value as NotificationTab);
    setStreamedNotifications([]);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      logEvent(ANALYTICS_EVENTS.NOTIFICATION_CENTER_OPENED);
    } else {
      logEvent(ANALYTICS_EVENTS.NOTIFICATION_CENTER_CLOSED);
    }
    setIsOpen(open);
    onDropdownOpenChange?.(open);

    if (open) {
      // Clear streamed notifications — fresh query data will include them
      setStreamedNotifications([]);
    }
    setActiveTab('unread');
  };

  const handleClose = () => {
    setIsOpen(false);
    onDropdownOpenChange?.(false);
  };

  const { mutateAsync: markAllAsRead } = useMarkNotificationsTillNowRead();

  const handleMarkAllAsRead = async () => {
    logEvent(ANALYTICS_EVENTS.NOTIFICATION_MARK_ALL_READ_CLICKED);
    try {
      // Find the latest create_time across all notifications (including streamed)
      const latestTime = notifications.reduce<string | undefined>(
        (latest, n) => {
          if (!n.create_time) return latest;
          if (!latest) return n.create_time;
          return n.create_time > latest ? n.create_time : latest;
        },
        undefined,
      );
      if (!latestTime) return;

      await markAllAsRead({
        organization: orgId,
        workspace: workspaceId,
        data: { read_time: latestTime },
      });

      queryClient.invalidateQueries({
        queryKey: getListUserNotificationsQueryKey(orgId, workspaceId),
      });
      setStreamedNotifications([]);
      invalidateUnreadCount();
    } catch {
      showToast(
        t('notifications.center.errors.markAllAsReadFailed'),
        undefined,
        'error',
      );
    }
  };

  const handleManageNotifications = () => {
    logEvent(ANALYTICS_EVENTS.NOTIFICATION_MANAGE_SETTINGS_CLICKED, '', {
      source: 'menu',
    });
    handleClose();
    navigate(
      getRoutePath('WORKSPACE_SETTINGS_NOTIFICATIONS', {
        orgId,
        workspaceId,
      }),
    );
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          className="app-sidebar-row"
          tooltip={t('notifications.center.title')}
          aria-label={isCollapsed ? t('notifications.center.title') : undefined}
        >
          <SidebarRowContent
            icon={
              <div className="relative">
                <Icon type="Bell" size={16} />
                {hasUnread && (
                  <div className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive" />
                )}
              </div>
            }
            label={t('notifications.center.title')}
            isCollapsed={isCollapsed}
          />
        </SidebarMenuButton>
      </PopoverTrigger>

      <PopoverContent
        className="w-[486px] h-[90vh] flex flex-col bg-background p-6"
        side="right"
        align="end"
        sideOffset={16}
        avoidCollisions
      >
        <Flex
          direction="column"
          width="full"
          gap={5}
          className="h-full min-h-0"
        >
          <Flex direction="column" gap={4} className="shrink-0 w-full">
            {/* Title and subtitle */}
            <Flex direction="column" gap={1.5}>
              <Text level="base" className="text-foreground" weight="semibold">
                {t('notifications.center.title')}
              </Text>
              <Text level="small" color="muted">
                {t('notifications.center.subtitle')}
              </Text>
            </Flex>

            {/* Tabs row: full-width TabsList + more options button */}
            <Flex gap={2} align="center" justify="between" className="w-full">
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="flex-1"
              >
                <TabsList className="w-2/3">
                  <TabsTrigger value="unread" className="flex-1">
                    {t('notifications.center.tabs.unread')}
                    {unreadCount > 0 && (
                      <Badge variant="default" className="ml-1 rounded-full">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="all" className="flex-1">
                    {t('notifications.center.tabs.all')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="shrink-0">
                    <Icon type="EllipsisVertical" size="sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onSelect={handleMarkAllAsRead}>
                    <Icon type="CircleCheck" size="sm" />
                    {t('notifications.center.menu.markAllAsRead')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleManageNotifications}>
                    <Icon type="Settings" size="sm" />
                    {t('notifications.center.menu.manageNotifications')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Flex>
          </Flex>

          <Flex className="flex-1 min-h-0 w-full overflow-y-auto">
            <NotificationList
              notifications={notifications}
              isLoading={isLoading || (isFetching && !isFetchingNextPage)}
              isError={isError}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage ?? false}
              onLoadMore={handleLoadMore}
              onClose={handleClose}
              onMarkAsRead={invalidateUnreadCount}
            />
          </Flex>
        </Flex>
      </PopoverContent>
    </Popover>
  );
}
